// src/studio/avatar/utils/prepareAvatarPostImages.js

function abortError() {
  const error = new Error('Image preparation cancelled.');
  error.name = 'AbortError';
  return error;
}

function checkAborted(signal) {
  if (signal?.aborted) {
    throw abortError();
  }
}

// Call when prepared attachments are discarded or removed.
export function releaseAvatarPostImages(images = []) {
  for (const image of images) {
    if (
      typeof image?.url === 'string' &&
      image.url.startsWith('blob:')
    ) {
      URL.revokeObjectURL(image.url);
    }
  }
}

function safeFileName(name, index) {
  const base = String(name || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

  return `${base || 'avatar'}-${index + 1}.png`;
}

async function validatePng(blob) {
  if (blob.size === 0) {
    throw new Error('The downloaded image is empty.');
  }

  const mimeType = blob.type
    .split(';')[0]
    .trim()
    .toLowerCase();

  if (mimeType !== 'image/png') {
    throw new Error('The downloaded file is not a PNG image.');
  }

  // Check the PNG signature as well as the response MIME type.
  const signature = new Uint8Array(
    await blob.slice(0, 8).arrayBuffer()
  );

  const expected = [137, 80, 78, 71, 13, 10, 26, 10];

  if (
    signature.length !== expected.length ||
    expected.some((byte, index) => signature[index] !== byte)
  ) {
    throw new Error(
      'The downloaded file has an invalid PNG signature.'
    );
  }
}

/**
 * Prepare saved avatars in selection order.
 *
 * Returns:
 * [
 *   { file: File, url: 'blob:...', source: 'studio_avatar' }
 * ]
 *
 * The caller owns the returned preview URLs and must release them
 * when removing attachments or leaving the composer.
 */
export async function prepareAvatarPostImages(
  creations,
  { signal } = {}
) {
  if (!Array.isArray(creations) || creations.length === 0) {
    throw new Error('Select at least one avatar to share.');
  }

  const prepared = [];

  try {
    // Sequential preparation keeps memory usage lower on mobile
    // and makes cleanup predictable if one image fails.
    for (let index = 0; index < creations.length; index += 1) {
      checkAborted(signal);

      const creation = creations[index];
      const label =
        typeof creation?.name === 'string' &&
        creation.name.trim()
          ? creation.name.trim()
          : `Avatar ${index + 1}`;

      try {
        const imageUrl = creation?.imageUrl;

        if (typeof imageUrl !== 'string' || !imageUrl.trim()) {
          throw new Error('No saved image URL is available.');
        }

        const parsedUrl = new URL(imageUrl.trim());

        if (!['https:', 'http:'].includes(parsedUrl.protocol)) {
          throw new Error('The saved image URL is not supported.');
        }

        // Fetch the public image directly.
        // Do not send the app's authorization token to Spaces.
        const response = await fetch(parsedUrl.href, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          signal,
        });

        if (!response.ok) {
          throw new Error(
            `Image download failed (HTTP ${response.status}).`
          );
        }

        const blob = await response.blob();

        checkAborted(signal);
        await validatePng(blob);
        checkAborted(signal);

        const file = new File(
          [blob],
          safeFileName(creation.name, index),
          {
            type: 'image/png',
            lastModified: Date.now(),
          }
        );

        prepared.push({
          file,
          url: URL.createObjectURL(file),
          source: 'studio_avatar',
        });
      } catch (error) {
        if (signal?.aborted || error?.name === 'AbortError') {
          throw abortError();
        }

        throw new Error(
          `Could not prepare "${label}". ${
            error?.message || 'Please try again.'
          }`
        );
      }
    }

    checkAborted(signal);

    return prepared;
  } catch (error) {
    // All-or-nothing: never return an incomplete selection.
    releaseAvatarPostImages(prepared);
    throw error;
  }
}
